import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  id: "why",
  class: "why-section"
}
const _hoisted_2 = { class: "shell" }
const _hoisted_3 = { class: "demo-card" }
const _hoisted_4 = { class: "control-row" }
const _hoisted_5 = { class: "render-grid" }
const _hoisted_6 = { class: "loss-panel" }
const _hoisted_7 = { class: "loss-copy" }
const _hoisted_8 = { class: "metrics" }
const _hoisted_9 = { class: "curve-wrap" }
const _hoisted_10 = { class: "continuation-block" }
const _hoisted_11 = { class: "demo-card continuation-card" }
const _hoisted_12 = { class: "control-row" }
const _hoisted_13 = { class: "continuation-grid" }
const _hoisted_14 = { class: "continuation-curve" }
const _hoisted_15 = { class: "curve-wrap" }
const _hoisted_16 = {
  class: "frame-strip",
  "aria-label": "Ten generated video frames"
}
const _hoisted_17 = {
  class: "continuation-progress",
  "aria-hidden": "true"
}
const _hoisted_18 = { class: "continuation-status" }

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const startX = 0.20
const targetX = 0.78
const radius = 0.13
const aspect = 480 / 250
const frameCount = 10
type Surface = {
  gl: WebGLRenderingContext
  program: WebGLProgram
  mode: number
}

const vertexSource = `
attribute vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
`

const fragmentSource = `
precision highp float;
uniform vec2 u_resolution;
uniform float u_x;
uniform float u_target;
uniform float u_final_target;
uniform float u_init;
uniform float u_radius;
uniform int u_mode;

float disk(vec2 uv, float cx) {
  float ar = u_resolution.x / u_resolution.y;
  vec2 p = vec2(uv.x * ar, uv.y);
  vec2 c = vec2(cx * ar, 0.5);
  float aa = 1.5 / u_resolution.y;
  return 1.0 - smoothstep(u_radius - aa, u_radius + aa, distance(p, c));
}

float lossAt(float cx, float target) {
  float ar = 1.92;
  float d = abs(cx - target) * ar;
  float r = u_radius;
  if (d >= 2.0 * r) return 1.0;
  float overlap = 2.0 * r * r * acos(d / (2.0 * r))
    - 0.5 * d * sqrt(max(0.0, 4.0 * r * r - d * d));
  return 1.0 - overlap / (3.14159265359 * r * r);
}

float continuationLoss(float cx) { return lossAt(cx, u_target); }

float lineMask(float value, float target, float width) {
  return 1.0 - smoothstep(width, width * 1.8, abs(value - target));
}

float segmentMask(vec2 p, vec2 a, vec2 b, float width) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return 1.0 - smoothstep(width, width * 1.7, length(pa - ba * h));
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 paper = vec3(0.965, 0.953, 0.925);
  vec3 ink = vec3(0.082, 0.137, 0.118);
  vec3 green = vec3(0.094, 0.310, 0.231);
  vec3 lime = vec3(0.804, 0.898, 0.420);
  vec3 orange = vec3(0.933, 0.490, 0.290);

  if (u_mode < 3) {
    float current = disk(uv, u_x);
    float target = disk(uv, u_target);
    float value = u_mode == 0 ? current : (u_mode == 1 ? target : abs(current - target));
    vec3 color = paper;
    if (u_mode == 2) {
      color = mix(vec3(0.075, 0.102, 0.090), orange, value);
    } else {
      color = mix(paper, u_mode == 0 ? green : ink, value);
    }
    gl_FragColor = vec4(color, 1.0);
    return;
  }

  vec3 color = paper;
  float left = 0.08;
  float right = 0.96;
  float bottom = 0.14;
  float top = 0.88;
  float axisX = lineMask(uv.x, left, 1.2 / u_resolution.x);
  float axisY = lineMask(uv.y, bottom, 1.2 / u_resolution.y);
  color = mix(color, ink, max(axisX * step(bottom, uv.y), axisY * step(left, uv.x)) * 0.65);

  for (int i = 1; i < 5; i++) {
    float gy = bottom + (top - bottom) * float(i) / 5.0;
    color = mix(color, ink, lineMask(uv.y, gy, 0.45 / u_resolution.y) * 0.10);
  }

  float dash = step(0.5, fract(uv.y * 28.0));
  float initGraphX = left + (u_init - 0.10) / 0.80 * (right - left);
  float finalGraphX = left + (u_final_target - 0.10) / 0.80 * (right - left);
  color = mix(color, ink, lineMask(uv.x, initGraphX, 1.2 / u_resolution.x) * dash * 0.22);
  color = mix(color, orange, lineMask(uv.x, finalGraphX, 1.2 / u_resolution.x) * dash * 0.34);

  float graphX = clamp((uv.x - left) / (right - left), 0.0, 1.0);
  float cx = mix(0.10, 0.90, graphX);
  float loss = continuationLoss(cx);
  float curveY = mix(bottom, top, loss);
  if (u_mode == 4) {
    float directY = mix(bottom, top, lossAt(cx, u_final_target));
    color = mix(color, orange, lineMask(uv.y, directY, 2.5 / u_resolution.y) * 0.24);
  }
  color = mix(color, green, lineMask(uv.y, curveY, 2.2 / u_resolution.y));

  vec2 point = vec2(
    left + (u_x - 0.10) / 0.80 * (right - left),
    mix(bottom, top, continuationLoss(u_x))
  );
  vec2 delta = (uv - point) * vec2(u_resolution.x / u_resolution.y, 1.0);
  float outerRadius = u_mode == 4 ? 0.045 : 0.020;
  float innerRadius = u_mode == 4 ? 0.032 : 0.010;
  float dotOuter = 1.0 - smoothstep(outerRadius, outerRadius + 0.004, length(delta));
  float dotInner = 1.0 - smoothstep(innerRadius, innerRadius + 0.003, length(delta));
  if (u_mode == 4) {
    vec2 scale = vec2(u_resolution.x / u_resolution.y, 1.0);
    vec2 tip = point + vec2(0.0, 0.050);
    vec2 top = point + vec2(0.0, 0.245);
    vec2 leftHead = point + vec2(-0.018, 0.078);
    vec2 rightHead = point + vec2(0.018, 0.078);
    float arrow = segmentMask(uv * scale, top * scale, tip * scale, 1.8 / u_resolution.y);
    arrow = max(arrow, segmentMask(uv * scale, leftHead * scale, tip * scale, 1.8 / u_resolution.y));
    arrow = max(arrow, segmentMask(uv * scale, rightHead * scale, tip * scale, 1.8 / u_resolution.y));
    color = mix(color, orange, arrow);
  }
  color = mix(color, paper, dotOuter);
  color = mix(color, orange, dotInner);
  gl_FragColor = vec4(color, 1.0);
}
`


export default /*@__PURE__*/_defineComponent({
  __name: 'WhyPlateau',
  setup(__props) {

const centerX = ref(startX)
const currentCanvas = ref<HTMLCanvasElement | null>(null)
const referenceCanvas = ref<HTMLCanvasElement | null>(null)
const errorCanvas = ref<HTMLCanvasElement | null>(null)
const curveCanvas = ref<HTMLCanvasElement | null>(null)
const videoCanvas = ref<HTMLCanvasElement | null>(null)
const trackedCanvas = ref<HTMLCanvasElement | null>(null)
const continuationCurveCanvas = ref<HTMLCanvasElement | null>(null)
const continuationT = ref(0)
const isPlaying = ref(false)

const frameCenters = Array.from(
  { length: frameCount },
  (_, index) => startX + (targetX - startX) * index / (frameCount - 1)
)
const sequenceState = computed(() => {
  if (continuationT.value <= 0) return { index: 0, local: 1 }
  if (continuationT.value >= 1) return { index: frameCount - 1, local: 1 }
  const scaled = continuationT.value * (frameCount - 1)
  const lowerFrame = Math.floor(scaled)
  const fraction = scaled - lowerFrame
  if (fraction < 0.000001) return { index: lowerFrame, local: 1 }
  return { index: Math.min(frameCount - 1, lowerFrame + 1), local: fraction }
})
const frameIndex = computed(() => sequenceState.value.index)
const frameNumber = computed(() => frameIndex.value + 1)
const intermediateX = computed(() => frameCenters[frameIndex.value])
const rollProgress = computed(() => Math.min(1, sequenceState.value.local / 0.78))
const trackedX = computed(() => {
  if (frameIndex.value === 0) return startX
  const from = frameCenters[frameIndex.value - 1]
  const to = frameCenters[frameIndex.value]
  const easedRoll = 1 - Math.pow(1 - rollProgress.value, 3)
  return from + (to - from) * easedRoll
})
const continuationStatus = computed(() => {
  if (continuationT.value >= 1) return 'Final target reached'
  if (frameIndex.value === 0) return 'Initialized at the first video frame'
  return `Optimizing frame ${frameNumber.value} of ${frameCount}`
})
const surfaces: Surface[] = []
const continuationSurfaces: Surface[] = []
let animationFrame: number | null = null

const normalizedLoss = (x: number): number => {
  const d = Math.abs(x - targetX) * aspect
  if (d >= 2 * radius) return 1
  const overlap = 2 * radius * radius * Math.acos(d / (2 * radius))
    - 0.5 * d * Math.sqrt(Math.max(0, 4 * radius * radius - d * d))
  return 1 - overlap / (Math.PI * radius * radius)
}

const plateauMse = 2 * Math.PI * radius * radius / aspect
const mse = computed(() => normalizedLoss(centerX.value) * plateauMse)
const gradient = computed(() => {
  const h = 0.0005
  return Math.abs((normalizedLoss(centerX.value + h) - normalizedLoss(centerX.value - h)) / (2 * h) * plateauMse)
})
const onPlateau = computed(() => Math.abs(centerX.value - targetX) * aspect >= 2 * radius)

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)
  if (!shader) throw new Error('Unable to create WebGL shader')
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) || 'WebGL shader compilation failed')
  }
  return shader
}

function createSurface(canvas: HTMLCanvasElement, mode: number): Surface {
  const gl = canvas.getContext('webgl', { antialias: true })
  if (!gl) throw new Error('WebGL is unavailable in this browser')
  const program = gl.createProgram()
  if (!program) throw new Error('Unable to create WebGL program')
  gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, vertexSource))
  gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource))
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) || 'WebGL program linking failed')
  }
  const buffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
  const position = gl.getAttribLocation(program, 'a_position')
  gl.enableVertexAttribArray(position)
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
  return { gl, program, mode }
}

function draw(): void {
  surfaces.forEach(({ gl, program, mode }) => {
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)
    gl.useProgram(program)
    gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), gl.canvas.width, gl.canvas.height)
    gl.uniform1f(gl.getUniformLocation(program, 'u_x'), centerX.value)
    gl.uniform1f(gl.getUniformLocation(program, 'u_target'), targetX)
    gl.uniform1f(gl.getUniformLocation(program, 'u_final_target'), targetX)
    gl.uniform1f(gl.getUniformLocation(program, 'u_init'), startX)
    gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius)
    gl.uniform1i(gl.getUniformLocation(program, 'u_mode'), mode)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
  })
}

function drawContinuation(): void {
  continuationSurfaces.forEach(({ gl, program, mode }) => {
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)
    gl.useProgram(program)
    gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), gl.canvas.width, gl.canvas.height)
    gl.uniform1f(gl.getUniformLocation(program, 'u_x'), trackedX.value)
    gl.uniform1f(gl.getUniformLocation(program, 'u_target'), intermediateX.value)
    gl.uniform1f(gl.getUniformLocation(program, 'u_final_target'), targetX)
    gl.uniform1f(gl.getUniformLocation(program, 'u_init'), startX)
    gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius)
    gl.uniform1i(gl.getUniformLocation(program, 'u_mode'), mode)
    gl.drawArrays(gl.TRIANGLES, 0, 6)
  })
}

function reset(): void { centerX.value = startX }

function pauseAnimation(): void {
  isPlaying.value = false
  if (animationFrame !== null) cancelAnimationFrame(animationFrame)
  animationFrame = null
}

function toggleAnimation(): void {
  if (isPlaying.value) {
    pauseAnimation()
    return
  }
  if (continuationT.value >= 1) continuationT.value = 0
  isPlaying.value = true
  const duration = 12000
  const startedAt = performance.now() - continuationT.value * duration
  const animate = (now: number): void => {
    continuationT.value = Math.min(1, (now - startedAt) / duration)
    if (continuationT.value < 1 && isPlaying.value) {
      animationFrame = requestAnimationFrame(animate)
    } else {
      isPlaying.value = false
      animationFrame = null
    }
  }
  animationFrame = requestAnimationFrame(animate)
}

onMounted(() => {
  const canvases = [currentCanvas.value, referenceCanvas.value, errorCanvas.value, curveCanvas.value]
  canvases.forEach((canvas, mode) => { if (canvas) surfaces.push(createSurface(canvas, mode)) })
  const continuationCanvases = [trackedCanvas.value, videoCanvas.value, continuationCurveCanvas.value]
  const continuationModes = [0, 1, 4]
  continuationCanvases.forEach((canvas, index) => {
    if (canvas) continuationSurfaces.push(createSurface(canvas, continuationModes[index]))
  })
  draw()
  drawContinuation()
})

watch(centerX, draw)
watch(continuationT, drawContinuation)
onBeforeUnmount(pauseAnimation)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[31] || (_cache[31] = _createElementVNode("div", { class: "why-heading" }, [
        _createElementVNode("div", null, [
          _createElementVNode("p", { class: "kicker" }, "Why continuation?"),
          _createElementVNode("h2", null, [
            _createTextVNode("A distant target can be"),
            _createElementVNode("br"),
            _createTextVNode("unreachable for gradient descent.")
          ])
        ]),
        _createElementVNode("p", null, " We optimize the position of a circle given the reference image. With a pixel-wise loss, two non-overlapping silhouettes produce the same error no matter how far apart they are. The objective is flat, so gradient descent has no direction to follow until the circles begin to overlap. ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", null, [
            _cache[2] || (_cache[2] = _createElementVNode("span", { class: "control-label" }, "circle center", -1)),
            _createElementVNode("b", null, "x = " + _toDisplayString(centerX.value.toFixed(3)), 1)
          ]),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((centerX).value = $event)),
            class: "position-slider",
            type: "range",
            min: "0.10",
            max: "0.90",
            step: "0.001",
            "aria-label": "Rendered circle x position"
          }, null, 512), [
            [
              _vModelText,
              centerX.value,
              void 0,
              { number: true }
            ]
          ]),
          _createElementVNode("button", {
            type: "button",
            onClick: reset
          }, "Reset")
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("figure", null, [
            _cache[3] || (_cache[3] = _createElementVNode("figcaption", null, [
              _createElementVNode("span", null, "01"),
              _createTextVNode(" Current render")
            ], -1)),
            _createElementVNode("canvas", {
              ref_key: "currentCanvas",
              ref: currentCanvas,
              width: "480",
              height: "250"
            }, null, 512),
            _cache[4] || (_cache[4] = _createElementVNode("p", null, "The optimizable circle", -1))
          ]),
          _createElementVNode("figure", null, [
            _cache[5] || (_cache[5] = _createElementVNode("figcaption", null, [
              _createElementVNode("span", null, "02"),
              _createTextVNode(" Reference image")
            ], -1)),
            _createElementVNode("canvas", {
              ref_key: "referenceCanvas",
              ref: referenceCanvas,
              width: "480",
              height: "250"
            }, null, 512),
            _cache[6] || (_cache[6] = _createElementVNode("p", null, "The fixed target on the right", -1))
          ]),
          _createElementVNode("figure", null, [
            _cache[7] || (_cache[7] = _createElementVNode("figcaption", null, [
              _createElementVNode("span", null, "03"),
              _createTextVNode(" Absolute error")
            ], -1)),
            _createElementVNode("canvas", {
              ref_key: "errorCanvas",
              ref: errorCanvas,
              width: "480",
              height: "250"
            }, null, 512),
            _cache[8] || (_cache[8] = _createElementVNode("p", null, "|render − reference|", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[12] || (_cache[12] = _createElementVNode("p", { class: "mini-kicker" }, "Pixel-wise MSE landscape", -1)),
            _cache[13] || (_cache[13] = _createElementVNode("h3", null, "Plateau in loss landscape.", -1)),
            _cache[14] || (_cache[14] = _createElementVNode("p", null, [
              _createTextVNode(" The highlighted point tracks the current circle position. On the plateau, changing "),
              _createElementVNode("em", null, "x"),
              _createTextVNode(" does not change the number of erroneous pixels, so the derivative is always zero -- our optimization can never leave the initial state! ")
            ], -1)),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", null, [
                _cache[9] || (_cache[9] = _createElementVNode("span", null, "MSE", -1)),
                _createElementVNode("b", null, _toDisplayString(mse.value.toFixed(4)), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[10] || (_cache[10] = _createElementVNode("span", null, "|∂L/∂x|", -1)),
                _createElementVNode("b", null, _toDisplayString(gradient.value.toFixed(4)), 1)
              ]),
              _createElementVNode("div", null, [
                _cache[11] || (_cache[11] = _createElementVNode("span", null, "Region", -1)),
                _createElementVNode("b", {
                  class: _normalizeClass({ active: !onPlateau.value })
                }, _toDisplayString(onPlateau.value ? 'Plateau' : 'Informative'), 3)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("canvas", {
              ref_key: "curveCanvas",
              ref: curveCanvas,
              width: "900",
              height: "300"
            }, null, 512),
            _cache[15] || (_cache[15] = _createElementVNode("span", { class: "axis-label y-label" }, "Loss", -1)),
            _cache[16] || (_cache[16] = _createElementVNode("span", { class: "axis-label x-label" }, "Circle center x", -1)),
            _cache[17] || (_cache[17] = _createElementVNode("span", { class: "target-label init-pose-label" }, [
              _createTextVNode("init x"),
              _createElementVNode("sub", null, "0")
            ], -1)),
            _cache[18] || (_cache[18] = _createElementVNode("span", { class: "target-label" }, "target x*", -1))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_10, [
        _cache[29] || (_cache[29] = _createElementVNode("div", { class: "continuation-intro" }, [
          _createElementVNode("div", null, [
            _createElementVNode("p", { class: "kicker" }, "Why continuation helps"),
            _createElementVNode("h2", null, "Idea: Follow a sequence of dynamic loss landscapes.")
          ]),
          _createElementVNode("p", null, " We generate a video that smoothly transitions from the initial state to the target image. By progressively using different video frames as optimization targets, we effectively induce a sequence of evolving loss landscapes throughout the optimization. Ideally, each successive landscape is locally smooth and well-behaved around the current solution, providing gradients that steer the parameters toward the final target. ")
        ], -1)),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", null, [
              _cache[19] || (_cache[19] = _createElementVNode("span", { class: "control-label" }, "Generated video sequence", -1)),
              _createElementVNode("b", null, "Frame " + _toDisplayString(frameNumber.value) + " / " + _toDisplayString(frameCount), 1)
            ]),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((continuationT).value = $event)),
              class: "position-slider",
              type: "range",
              min: "0",
              max: "1",
              step: "0.001",
              "aria-label": "Continuation video time",
              onInput: pauseAnimation
            }, null, 544), [
              [
                _vModelText,
                continuationT.value,
                void 0,
                { number: true }
              ]
            ]),
            _createElementVNode("button", {
              type: "button",
              onClick: toggleAnimation
            }, _toDisplayString(isPlaying.value ? 'Pause' : continuationT.value >= 1 ? 'Replay' : 'Play video'), 1)
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("figure", null, [
              _cache[22] || (_cache[22] = _createElementVNode("figcaption", null, [
                _createElementVNode("span", null, "01"),
                _createTextVNode(" Generated video frame")
              ], -1)),
              _createElementVNode("canvas", {
                ref_key: "videoCanvas",
                ref: videoCanvas,
                width: "480",
                height: "250"
              }, null, 512),
              _createElementVNode("p", null, [
                _cache[20] || (_cache[20] = _createTextVNode("Discrete reference at x", -1)),
                _cache[21] || (_cache[21] = _createElementVNode("sub", null, "t", -1)),
                _createTextVNode(" = " + _toDisplayString(intermediateX.value.toFixed(3)), 1)
              ])
            ]),
            _createElementVNode("figure", null, [
              _cache[23] || (_cache[23] = _createElementVNode("figcaption", null, [
                _createElementVNode("span", null, "02"),
                _createTextVNode(" Tracked solution")
              ], -1)),
              _createElementVNode("canvas", {
                ref_key: "trackedCanvas",
                ref: trackedCanvas,
                width: "480",
                height: "250"
              }, null, 512),
              _cache[24] || (_cache[24] = _createElementVNode("p", null, "The optimized circle follows the moving minimum", -1))
            ]),
            _createElementVNode("figure", _hoisted_14, [
              _cache[26] || (_cache[26] = _createElementVNode("figcaption", null, [
                _createElementVNode("span", null, "03"),
                _createTextVNode(" Advected loss landscape")
              ], -1)),
              _createElementVNode("div", _hoisted_15, [
                _createElementVNode("canvas", {
                  ref_key: "continuationCurveCanvas",
                  ref: continuationCurveCanvas,
                  width: "900",
                  height: "300"
                }, null, 512),
                _cache[25] || (_cache[25] = _createStaticVNode("<span class=\"axis-label y-label\" data-v-5b0cf0e8>Loss</span><span class=\"axis-label x-label\" data-v-5b0cf0e8>Circle center x</span><span class=\"target-label init-pose-label\" data-v-5b0cf0e8>init x<sub data-v-5b0cf0e8>0</sub></span><span class=\"target-label\" data-v-5b0cf0e8>target x*</span><span class=\"curve-legend\" data-v-5b0cf0e8><i data-v-5b0cf0e8></i>Current frame <i data-v-5b0cf0e8></i>Direct final reference</span>", 5))
              ]),
              _cache[27] || (_cache[27] = _createElementVNode("p", null, "The point rises after each jump, then rolls down the new basin", -1))
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(frameCenters), (_, index) => {
              return (_openBlock(), _createElementBlock("span", {
                key: index,
                class: _normalizeClass({ complete: index < frameIndex.value, active: index === frameIndex.value })
              }, _toDisplayString(String(index + 1).padStart(2, '0')), 3))
            }), 128))
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("span", {
              style: _normalizeStyle({ width: `${continuationT.value * 100}%` })
            }, null, 4)
          ]),
          _createElementVNode("p", _hoisted_18, [
            _createElementVNode("b", null, _toDisplayString(continuationStatus.value), 1),
            _createElementVNode("span", null, [
              _createTextVNode("local solve " + _toDisplayString(Math.round(rollProgress.value * 100)) + "% · x = " + _toDisplayString(trackedX.value.toFixed(3)) + " → x", 1),
              _cache[28] || (_cache[28] = _createElementVNode("sub", null, "t", -1)),
              _createTextVNode(" = " + _toDisplayString(intermediateX.value.toFixed(3)), 1)
            ])
          ])
        ]),
        _cache[30] || (_cache[30] = _createElementVNode("div", { class: "takeaway" }, [
          _createElementVNode("span", null, "Key observation"),
          _createElementVNode("p", null, " The target image says what the solution should look like, but not how to reach it. Video numerical continuation supplies intermediate references that tell how parameters should actually evolve toward the optimizer. ")
        ], -1))
      ])
    ])
  ]))
}
}

})